import useStyles from 'isomorphic-style-loader/useStyles';
import { MoveRight } from 'lucide-react';
import { useState } from 'react';
import { Button, Icon, Loader, Popup } from 'semantic-ui-react';

import { extractSearchPages } from '@store/search';
import Err from '@components/err';
import { InfiniteScroll } from '@components/infinite-scroll';
import Link from '@components/link';
import { PopupPanel } from '@components/popup';
import {
  ArtifactTypeLabel,
  getImageAnalysisPath,
  getPrimaryArtifact,
  isImageArtifact,
} from '@features/artifact/common/artifact-type';
import { AssetTypeLabel } from '@features/artifact/common/asset-type';

import { useListAssetsByPackageInfiniteQuery } from '../applications.api';
import type { Asset } from '../applications.models';

import s from './affected-assets-popup.scss';

const AssetRow = ({ asset }: { asset: Asset }) => {
  const artifact = getPrimaryArtifact(asset);
  const artifactType = artifact?.type;
  const imageHref =
    artifact && isImageArtifact(artifact) && asset.reference
      ? getImageAnalysisPath({
          digest: artifact.reference.digest,
          reference: asset.reference,
        })
      : null;

  return (
    <div className={s.asset}>
      <span className={s.assetName}>
        {imageHref ? (
          <Popup
            role="tooltip"
            position="top center"
            size="mini"
            content={
              <span>
                Go to image analysis <MoveRight />
              </span>
            }
            trigger={
              <Link
                to={imageHref}
                aria-label={`Go to image analysis for ${asset.name}`}
                muted
              >
                {asset.name}
              </Link>
            }
            mouseEnterDelay={250}
            basic
          />
        ) : (
          asset.name
        )}
      </span>

      <div>
        {artifactType && <ArtifactTypeLabel compact type={artifactType} />}

        <AssetTypeLabel compact type={asset.type} />
      </div>
    </div>
  );
};

export interface AffectedAssetsPopupProps {
  /**
   * Application owning the version whose assets are listed.
   */
  appId: string;

  /**
   * Number of assets affected by this package.
   */
  count: number;

  /**
   * Package name used to query assets.
   */
  name: string;

  /**
   * Package namespace used to disambiguate same-name packages.
   */
  namespace?: string;

  /**
   * Package type (e.g. npm, apk, python).
   */
  type: string;

  /**
   * Package version.
   */
  version: string;

  /**
   * Application version whose assets are listed.
   */
  versionId: string;
}

/**
 * Renders a button showing the affected asset count. When clicked, opens a
 * popup that lazily fetches and displays the list of assets affected by the
 * given package.
 */
export const AffectedAssetsPopup = ({
  appId,
  count,
  name,
  namespace,
  type,
  version,
  versionId,
}: AffectedAssetsPopupProps) => {
  useStyles(s);

  const [isOpen, setIsOpen] = useState(false);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useListAssetsByPackageInfiniteQuery(
    { appId, name, namespace, type, version, versionId },
    { skip: !isOpen },
  );

  const assets = extractSearchPages(data);

  const disabled = count === 0;

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  return (
    <Popup
      className={s.popup}
      disabled={disabled}
      on="click"
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      position="left center"
      trigger={
        <Button
          className={s.trigger}
          aria-label={`${count} affected assets`}
          compact
          disabled={disabled}
          primary
          basic
          size="mini"
        >
          {count}
        </Button>
      }
    >
      <PopupPanel
        icon={<Icon name="dna" color="teal" />}
        title="Affected Assets"
      >
        {error ? (
          <div className={s.loader}>
            <Err error={error} />
          </div>
        ) : isLoading ? (
          <div className={s.loader}>
            <Loader active inline="centered" size="small" />
          </div>
        ) : (
          <div className={s.scroll}>
            <InfiniteScroll
              hasMore={hasNextPage}
              hasScrollableParent
              isLoading={isFetchingNextPage || (isFetching && !isLoading)}
              loadMore={() => {
                fetchNextPage();
              }}
            >
              <div className={s.list}>
                {assets.map(asset => (
                  <AssetRow key={asset.systemMetadata.id} asset={asset} />
                ))}
              </div>
            </InfiniteScroll>
          </div>
        )}
      </PopupPanel>
    </Popup>
  );
};

export default AffectedAssetsPopup;
